import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  Chip,
  ImageList,
  ImageListItem,
  IconButton,
  Dialog,
  DialogContent,
  CircularProgress,
  Divider,
  Alert,
  DialogTitle,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Euro as EuroIcon,
  LocationOn as LocationIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Message as MessageIcon,
  Flag as FlagIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { C } from '../theme';
import { useAuth } from '../contexts/AuthContext';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import { notifyMessageSent } from '../utils/messageEvents';
import { countries } from '../data/locations';
import SEO from '../components/SEO';
import LocationMap from '../components/LocationMap';
import MediaPlaceholder from '../components/MediaPlaceholder';
import TerrainOutlinedIcon from '@mui/icons-material/TerrainOutlined';

interface AdImage {
  id: number;
  filename: string;
  thumbnail_filename: string;
  order_index: number;
}

interface Ad {
  id: number;
  title: string;
  description: string;
  price: number;
  country_id: string;
  city_id: string;
  land_type: string;
  area: number;
  user_id: number;
  author_name: string;
  created_at: string;
  images: AdImage[];
  deleted: boolean;
  latitude: number | string | null;
  longitude: number | string | null;
}

const landTypeLabelMap: Record<string, string> = {
  agricultural: 'Poljoprivredno zemljište',
  residential: 'Gradsko zemljište',
  commercial: 'Šumsko zemljište',
  industrial: 'Zemljište za izgradnju',
  recreational: 'Zemljište za rekreaciju',
};

const AdDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [ad, setAd] = useState<Ad | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportSuccess, setReportSuccess] = useState('');
  const [blockLoading, setBlockLoading] = useState(false);

  useEffect(() => {
    const fetchAd = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/ads/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setAd(response.data);
      } catch (err) {
        setError('Greška pri učitavanju detalja oglasa');
      } finally {
        setLoading(false);
      }
    };

    // Redirect non-authenticated users to login
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    fetchAd();
  }, [id, isAuthenticated, navigate]);

  const handleDelete = async () => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovaj oglas?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
              await axios.delete(`/api/ads/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate('/my-ads');
    } catch (err) {
      setError('Greška pri brisanju oglasa');
    }
  };

  const handleImageClick = (filename: string) => {
    setSelectedImage(filename);
  };

  const handleCloseImage = () => {
    setSelectedImage(null);
  };

  const handleMessageClick = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (ad) {
      notifyMessageSent();
      navigate(`/messages?adId=${ad.id}&receiverId=${ad.user_id}&sendInitial=1`);
    }
  };

  const handleReportClick = () => {
    setReportDialogOpen(true);
    setReportReason('');
    setReportSuccess('');
    setError('');
  };

  const handleReportSubmit = async () => {
    if (!reportReason.trim()) {
      setError('Molimo unesite razlog prijave');
      return;
    }

    setReportLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/moderation/report',
        {
          content_type: 'ad',
          content_id: ad?.id,
          reported_user_id: ad?.user_id,
          reason: reportReason,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setReportSuccess('Oglas je uspešno prijavljen. Naš tim će pregledati prijavu u roku od 24 sata.');
      setReportReason('');
      setTimeout(() => {
        setReportDialogOpen(false);
        setReportSuccess('');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Greška pri prijavljivanju oglasa');
    } finally {
      setReportLoading(false);
    }
  };

  const handleBlockUser = async () => {
    if (!window.confirm('Da li ste sigurni da želite da blokirate ovog korisnika? Više nećete videti njihove oglase i poruke.')) {
      return;
    }

    setBlockLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/moderation/block',
        {
          blocked_id: ad?.user_id,
          reason: 'Blokiran putem oglasa',
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      alert('Korisnik je uspešno blokiran. Više nećete videti njihov sadržaj.');
      navigate('/nekretnine');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Greška pri blokiranju korisnika');
    } finally {
      setBlockLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !ad) {
    return (
      <Box sx={{ pt: '90px', pb: 8, backgroundColor: C.bg, minHeight: '100vh' }}>
        <Container maxWidth="md">
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center', backgroundColor: C.surface, borderRadius: 4, border: `1px solid ${C.line}` }}>
            <Typography color="error">{error || 'Oglas nije pronađen'}</Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  const country = countries.find(c => c.id === Number(ad.country_id || 0));
  const city = country?.cities.find(c => c.id === Number(ad.city_id || 0));
  const isOwner = user?.id === ad.user_id;

  return (
    <Box sx={{ pt: '90px', pb: 8, backgroundColor: C.bg, minHeight: '100vh' }}>
      <SEO
        title={ad.title}
        description={`${landTypeLabelMap[ad.land_type] || 'Zemljište'}${ad.area ? `, ${ad.area} m²` : ''} • ${ad.price?.toLocaleString('sr-RS')} € • Oglas za zemljište na platformi Nađi Investitora. ${(ad.description || '').replace(/\s+/g, ' ').trim().slice(0, 110)}`.slice(0, 300)}
        url={`/ads/${ad.id}`}
        image={ad.images?.[0] ? `/api/ads/uploads/${ad.images[0].filename}` : undefined}
        type="article"
      />
      <Container maxWidth="lg">
        {Boolean(ad.deleted) && (
          <Alert severity="warning" sx={{ mb: 4 }}>
            Ovaj oglas je obrisan. Više nije dostupan javno, ali možete videti detalje zbog istorije poruka.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={0} sx={{ backgroundColor: C.surface, borderRadius: 4, border: `1px solid ${C.line}` }}>
          <Grid container>
            {/* Images Section */}
            <Grid item xs={12} md={8}>
              {ad.images.length > 0 ? (
                <ImageList sx={{ width: '100%', height: 500, m: 0 }} cols={2} rowHeight={250}>
                  {ad.images.map((image, index) => (
                    <ImageListItem key={image.id}>
                      <img
                        src={`/api/ads/uploads/thumbnails/${image.thumbnail_filename}`}
                        alt={ad.title || `ad-image-${index}`}
                        loading="lazy"
                        onClick={() => handleImageClick(image.filename)}
                        style={{ cursor: 'pointer', height: '100%', objectFit: 'cover' }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              ) : (
                <MediaPlaceholder
                  height={500}
                  icon={<TerrainOutlinedIcon sx={{ fontSize: 72, color: 'rgba(243,192,21,0.45)' }} />}
                  label="Bez fotografije"
                />
              )}
            </Grid>

            {/* Details Section */}
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 4 }}>
                <Typography variant="h3" sx={{ fontWeight: 600, mb: 3, color: C.white, lineHeight: 1.2 }}>
                  {ad.title}
                </Typography>

                <Box sx={{ mb: 4 }}>
                  <Chip
                    icon={<EuroIcon />}
                    label={`${ad.price.toLocaleString()} €`}
                    sx={{
                      background: C.goldGrad,
                      color: C.onGold,
                      fontWeight: 700,
                      fontSize: '1rem',
                      borderRadius: 0,
                      mb: 2,
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationIcon sx={{ color: C.muted, mr: 1 }} />
                    <Typography variant="body1" sx={{ color: C.muted, fontWeight: 500 }}>
                      {city?.name}, {country?.name}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ mb: 4, borderColor: C.line }} />

                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: C.white }}>
                    Detalji oglasa
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: C.muted, mb: 0.5 }}>
                        Tip zemljišta
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: C.white }}>
                        {landTypeLabelMap[ad.land_type] || ad.land_type}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: C.muted, mb: 0.5 }}>
                        Površina
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: C.white }}>
                        {ad.area.toLocaleString()} m²
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ mb: 4, borderColor: C.line }} />

                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: C.white }}>
                    Kontakt informacije
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon sx={{ color: C.muted, mr: 1 }} />
                    <Typography variant="body1" sx={{ color: C.muted }}>
                      {ad.author_name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CalendarIcon sx={{ color: C.muted, mr: 1 }} />
                    <Typography variant="body1" sx={{ color: C.muted }}>
                      Objavljeno: {new Date(ad.created_at).toLocaleDateString('sr-RS')}
                    </Typography>
                  </Box>
                </Box>

                {/* Action Buttons */}
                {isOwner ? (
                  <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => navigate(`/edit-ad/${ad.id}`)}
                      sx={{
                        borderColor: 'rgba(243,192,21,0.55)',
                        color: C.white,
                        '&:hover': {
                          background: C.goldGrad,
                          color: C.onGold,
                        },
                      }}
                    >
                      Izmeni
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                      onClick={handleDelete}
                      sx={{
                        borderColor: C.danger,
                        color: C.danger,
                        '&:hover': {
                          backgroundColor: C.danger,
                          color: C.onGold,
                        },
                      }}
                    >
                      Obriši
                    </Button>
                  </Box>
                ) : (
                  // Only show message button for investors (not land owners)
                  <>
                    {user?.userType === 'investor' && (
                      <Box sx={{ mb: 2 }}>
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<MessageIcon />}
                          onClick={handleMessageClick}
                          sx={{
                            background: C.goldGrad,
                            py: 1.5,
                            '&:hover': {
                              filter: 'brightness(1.06)',
                            },
                          }}
                        >
                          Pošalji poruku vlasniku
                        </Button>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
                      <Button
                        variant="outlined"
                        startIcon={<FlagIcon />}
                        onClick={handleReportClick}
                        sx={{
                          flex: 1,
                          borderColor: 'rgba(243,192,21,0.55)',
                          color: '#F6CB4B',
                          '&:hover': {
                            background: C.goldGrad,
                            color: C.onGold,
                            borderColor: 'rgba(243,192,21,0.55)',
                          },
                        }}
                      >
                        Prijavi
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<BlockIcon />}
                        onClick={handleBlockUser}
                        disabled={blockLoading}
                        sx={{
                          flex: 1,
                          borderColor: C.danger,
                          color: C.danger,
                          '&:hover': {
                            backgroundColor: C.danger,
                            color: C.onGold,
                            borderColor: C.danger,
                          },
                        }}
                      >
                        {blockLoading ? 'Blokiranje...' : 'Blokiraj'}
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Description Section */}
          <Box sx={{ p: 4, borderTop: `1px solid ${C.line}` }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: C.white }}>
              Opis
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8, color: C.muted }}>
              {ad.description}
            </Typography>
          </Box>

          {/* Location Section — backend returns coordinates only to the owner or investors */}
          {ad.latitude != null && ad.longitude != null && (
            <Box sx={{ p: 4, borderTop: `1px solid ${C.line}` }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: C.white }}>
                Lokacija
              </Typography>
              <LocationMap lat={Number(ad.latitude)} lng={Number(ad.longitude)} height={320} />
            </Box>
          )}
        </Paper>

        {/* Image Modal */}
        <Dialog
          open={!!selectedImage}
          onClose={handleCloseImage}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent sx={{ p: 0, position: 'relative' }}>
            <IconButton
              onClick={handleCloseImage}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.7)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            {selectedImage && (
              <img
                src={`/api/ads/uploads/${selectedImage}`}
                alt="Full size"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Report Dialog */}
        <Dialog
          open={reportDialogOpen}
          onClose={() => setReportDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Prijavi oglas</DialogTitle>
          <DialogContent>
            {reportSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {reportSuccess}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Typography variant="body2" sx={{ mb: 2, color: C.muted }}>
              Molimo vas da opišete razlog prijave. Naš tim će pregledati prijavu u roku od 24 sata.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Razlog prijave"
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              placeholder="Npr. Uvredljiv sadržaj, lažne informacije, spam..."
              disabled={reportLoading || !!reportSuccess}
            />
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setReportDialogOpen(false)}
              disabled={reportLoading}
            >
              Otkaži
            </Button>
            <Button
              onClick={handleReportSubmit}
              variant="contained"
              disabled={reportLoading || !!reportSuccess}
              sx={{
                background: C.goldGrad,
                color: C.onGold,
                '&:hover': {
                  filter: 'brightness(1.06)',
                },
              }}
            >
              {reportLoading ? <CircularProgress size={24} /> : 'Prijavi'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdDetails; 